/**
 * The "Leaderboard" card on a leaderboard game's Final Results: whether the result has reached the board, and
 * what to do if it hasn't. Drawn into the `#leaderboardStatus` element that `renderSummary` provides, and
 * redrawn in place as a send progresses — re-rendering the whole results screen would restart its confetti and PDF.
 */

import type { Game } from '../../game/types';
import { esc } from '../helpers';
import { describeNotCounted } from '../leaderboardClient';
import type { SubmissionStatus } from '../leaderboardSubmit';

export interface LeaderboardStatusActions {
  /** Sends the result now (the button offered when it is still owed). */
  send(): void;
  viewBoard(): void;
}

/** The card's inner HTML for a status (exported so it can be tested without a DOM). */
export function leaderboardStatusHtml(game: Game, status: SubmissionStatus): string {
  const lb = game.leaderboard;
  if (!lb) return '';
  const notCounted =
    lb.notCounted.length > 0
      ? `<p class="muted">Not added to the board: ${esc(describeNotCounted(lb.notCounted).join('; '))}.</p>`
      : '';
  switch (status.kind) {
    case 'sent':
      return `
        <p><strong>✓ Sent to the ${esc(lb.venue)} leaderboard.</strong></p>
        ${notCounted}
        <button type="button" id="lbViewBoardBtn" class="secondary" style="width:100%">View the leaderboard</button>`;
    case 'sending':
      return `<p><strong>Sending the result to the leaderboard…</strong></p>`;
    case 'owed':
      return `
        <p><strong>This result hasn't reached the leaderboard yet.</strong></p>
        <p class="muted">${status.reason ? `${esc(status.reason)} ` : ''}It is kept on this device and sent again
        automatically, or you can send it now.</p>
        <button type="button" id="lbSendBtn" style="width:100%">Send to the leaderboard</button>`;
    case 'rejected':
      return `
        <p><strong>The leaderboard did not accept this result.</strong></p>
        <p class="muted">${esc(status.message)}</p>
        <button type="button" id="lbViewBoardBtn" class="ghost" style="width:100%">View the leaderboard</button>`;
  }
}

/**
 * (Re)draws the card, and hides "go back to this game" while the result is being sent and once it is on the
 * board — an accepted result can't be taken back, so reopening the game would only let the two disagree. A no-op if the results screen
 * isn't the one showing.
 */
export function paintLeaderboardStatus(
  root: HTMLElement,
  game: Game,
  status: SubmissionStatus,
  actions: LeaderboardStatusActions,
): void {
  const box = root.querySelector<HTMLElement>('#leaderboardStatus');
  if (!box) return;
  box.innerHTML = leaderboardStatusHtml(game, status);
  box.querySelector<HTMLButtonElement>('#lbSendBtn')?.addEventListener('click', () => actions.send());
  box.querySelector<HTMLButtonElement>('#lbViewBoardBtn')?.addEventListener('click', () => actions.viewBoard());
  const resume = root.querySelector<HTMLElement>('#resumeGameBtn');
  if (resume) resume.style.display = status.kind === 'sent' || status.kind === 'sending' ? 'none' : '';
}
